import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { Empty, ErrorBox, Field, Loading, Notice, PageHeader, fmtDate } from '../components/ui';

interface Call {
  id: string;
  title: string;
  starts_at: string;
  duration_minutes: number;
  include_children: boolean;
  record_call: boolean;
  status: string;
  grandparent_name: string;
  relation_label: string | null;
}

export function CallsPage() {
  const { user } = useAuth();
  const calls = useApi<{ calls: Call[] }>('/calls');
  const tree = useApi<{ people: Array<{ id: string; display_name: string; relation_label: string | null; is_living: boolean; generation: number; child_profile_id: string | null }> }>('/tree');
  const act = useAction();
  const [form, setForm] = useState({ grandparentPersonId: '', title: 'Sunday call with Grandma', startsAt: '', durationMinutes: 30, includeChildren: true, recordCall: false });
  const elders = (tree.data?.people ?? []).filter((p) => p.generation >= 2 && p.is_living && !p.child_profile_id);

  return (
    <>
      <PageHeader title="Grandparent Calls" termKey="page.calls" lead="Private family video calls, scheduled and hosted by a parent. Optionally record Grandma’s voice for the Story Vault." />
      <Notice text={act.notice} />
      <ErrorBox error={act.error ?? calls.error} />
      <div className="grid grid-2">
        <section className="stack">
          {calls.loading && <Loading />}
          {calls.data?.calls.length === 0 && <Empty>No calls scheduled.</Empty>}
          {calls.data?.calls.map((c) => (
            <div className="card stack" key={c.id}>
              <strong className="yo">{c.title}</strong>
              <span className="small muted">
                with <span className="yo">{c.relation_label} {c.grandparent_name}</span> · {fmtDate(c.starts_at)} · {c.duration_minutes} min {c.record_call && '· 🎙️ recording'}
              </span>
              <div className="row">
                <button
                  className="btn small"
                  onClick={async () => {
                    const r = await act.run(() => api<{ joinUrl: string }>(`/calls/${c.id}/join`, { method: 'POST', body: {} }));
                    if (r) window.open(r.joinUrl, '_blank', 'noopener');
                  }}
                >
                  Join call
                </button>
                {user?.role === 'parent' && (
                  <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/calls/${c.id}/cancel`, { method: 'POST', body: {} }), 'Cancelled.'); calls.reload(); }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </section>
        {user?.role === 'parent' && (
          <form
            className="card stack"
            onSubmit={async (e) => {
              e.preventDefault();
              await act.run(() => api('/calls', { body: { ...form, startsAt: new Date(form.startsAt).toISOString() } }), 'Call scheduled. Share the time with Grandma!');
              calls.reload();
            }}
          >
            <h2>Schedule a call</h2>
            <Field id="gp" label="Who are we calling?">
              <select id="gp" value={form.grandparentPersonId} onChange={(e) => setForm({ ...form, grandparentPersonId: e.target.value })} required>
                <option value="">Choose…</option>
                {elders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.relation_label} {p.display_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="ct" label="Title">
              <input id="ct" className="yo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </Field>
            <Field id="cs" label="When">
              <input id="cs" type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} required />
            </Field>
            <label className="check">
              <input type="checkbox" checked={form.includeChildren} onChange={(e) => setForm({ ...form, includeChildren: e.target.checked })} /> Children can join (on a parent’s device)
            </label>
            <label className="check">
              <input type="checkbox" checked={form.recordCall} onChange={(e) => setForm({ ...form, recordCall: e.target.checked })} /> Record this call to keep Grandma’s voice (everyone is told at the start)
            </label>
            <button className="btn" disabled={act.busy}>
              Schedule
            </button>
          </form>
        )}
      </div>
    </>
  );
}
